import React from 'react';
import clsx from 'clsx';
import { ClipboardIcon } from '@heroicons/react/24/outline';

import CopyToClipboard from '@dashboard/components/CopyToClipboard';
import { Button } from '@dashboard/components/ui/button';
import { Table, TableBody, TableCell, TableRow } from '@dashboard/components/ui/table';
import { toast } from '@dashboard/context/ToastContext';

// A row sets `copy` to the text to copy, or to null for nothing; otherwise a plain value copies as is.
export const copyTextOf = row => {
  if ('copy' in row) return row.copy || null;
  return typeof row.value === 'string' || typeof row.value === 'number' ? String(row.value) : null;
};

export const CopyButton = ({ text, className }) => (
  <CopyToClipboard text={text} onCopy={() => toast({ title: 'Copied', type: 'success' })}>
    <Button
      variant="ghost"
      size="icon-xs"
      aria-label="Copy"
      className={clsx(
        'text-gray-500 dark:text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100',
        className,
      )}
    >
      <ClipboardIcon />
    </Button>
  </CopyToClipboard>
);

/**
 * Label-left detail table from the confirmation emails: dark label over a grey helper, value flush right, a hairline per
 * row, a copy button on hover. Rows: { label, sub?, value, copy?, rail?, trail?, onOpen?, full? }; `rail` sits left
 * of the value, `trail` right of it. A row with `onOpen` opens on a click anywhere but its own controls, and a
 * `full` row is its value alone across the table.
 */
const OrderDetailsTable = ({ rows }) => (
  <div className="rounded-lg border border-gray-200 dark:border-border mt-3 overflow-hidden">
    <Table>
      <TableBody className="[&_tr:last-child]:border-0">
        {rows.map((row, index) => {
          if (row.full) {
            return (
              <TableRow key={row.key ?? index} className="hover:bg-transparent">
                <TableCell colSpan={2} className="px-4 py-3 whitespace-normal">
                  {row.value}
                </TableCell>
              </TableRow>
            );
          }
          const text = copyTextOf(row);
          return (
            <TableRow
              key={row.key ?? index}
              className={clsx('group', row.onOpen ? 'cursor-pointer' : 'hover:bg-transparent')}
              onClick={
                row.onOpen
                  ? event => {
                      if (event.target.closest('button, a, [role="menuitem"]')) return;
                      row.onOpen();
                    }
                  : undefined
              }
            >
              <TableCell className="pl-4 py-3 align-middle text-gray-900 dark:text-foreground w-[30%] whitespace-normal">
                {row.label}
                {row.sub ? (
                  <div className="text-xs text-gray-500 dark:text-muted-foreground">{row.sub}</div>
                ) : null}
              </TableCell>
              <TableCell className="pr-4 py-3 align-middle whitespace-normal">
                <div className="flex items-center justify-end gap-x-2">
                  {text ? <CopyButton text={text} /> : null}
                  {row.rail || null}
                  <div className="min-w-0 text-right wrap-anywhere">{row.value}</div>
                  {row.trail || null}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);

export default OrderDetailsTable;
